import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useCapabilities } from '../context/CapabilityContext';
import { useFeatures } from '../context/FeatureContext';
import CapabilityForbidden from '../jsx/pages/CapabilityForbidden';

/**
 * <CapabilityRoute> — Phase 1.3 of the access-control redesign.
 *
 * Composes with <FeatureRoute> via the optional `feature` prop on this
 * guard. When set, the entitlement check runs FIRST (matching FeatureRoute's
 * behavior — redirect to /:companyId/ on missing feature) before the
 * capability check. The two layers stack: a tenant whose plan excludes
 * `time_tracking` redirects to home; a tenant who has the feature but
 * whose user lacks the scope renders the 403 page. We collapsed the
 * sibling-Route pattern (which was incompatible with React Router's Switch
 * matching the FIRST route by path) into a single guard so consumers can
 * mount one route per surface.
 *
 * Required props (one of):
 *   - `capability`: a (module, action, level) tuple as a single string,
 *     e.g., "time.view.team". The route renders Component iff
 *     hasScope(module, action, level) is true.
 *   - `requireRole`: a role boolean from the capability response, e.g.,
 *     "isOwner" | "isAdmin" | "isPayrollAdmin" | "isHrAdmin". The route
 *     renders Component iff capabilities[requireRole] is true.
 *
 * Optional props:
 *   - `feature`: if set, run an entitlement gate first via FeatureContext.
 *     Missing feature redirects to /:companyId/ (mirrors FeatureRoute
 *     exactly so behavior is identical to today's no-capability mounts).
 *   - `module`: passed through to the 403 page so the user sees what they
 *     were trying to reach (e.g. "time-reporting"). Defaults to the
 *     `capability` module segment when not provided.
 *   - `redirectTo`: if set, the guard redirects there instead of rendering
 *     the 403 page. Used sparingly — the default 403 page IS the
 *     advertised UX. Useful for cases like the *Mina* tab when the caller
 *     has no employee mapping (sidebar should have hidden the link
 *     anyway, redirect home as a defense-in-depth net).
 *
 * Loading semantics:
 *   - While capabilities are still loading we render nothing. Same pattern
 *     as FeatureRoute — avoids a flash of the 403 page during initial
 *     fetch, especially right after login when the capabilities request
 *     is still inflight.
 *   - On fetch error the context returns fail-closed defaults; the route
 *     therefore renders the 403 page, which lets the user click through
 *     to home rather than being stuck on a spinner.
 *
 * Example mounts:
 *
 *   <CapabilityRoute
 *     path="/:companyId/time-reporting/approval"
 *     capability="time.approve.team"
 *     module="time-reporting.approve"
 *     component={ApprovalPage}
 *   />
 *
 *   <CapabilityRoute
 *     path="/:companyId/settings/billing/cancel"
 *     requireRole="isOwner"
 *     module="admin"
 *     redirectTo="/:companyId/settings"
 *     component={CancelSubscriptionPage}
 *   />
 */
export const CapabilityRoute = ({
  component: Component,
  capability,
  requireRole,
  feature,
  module: blockedModule,
  redirectTo,
  ...rest
}) => {
  const { capabilities, hasLoaded, hasScope } = useCapabilities();
  const { features, hasLoaded: featuresLoaded } = useFeatures();

  return (
    <Route
      {...rest}
      render={(props) => {
        // Wait for BOTH gates' data before rendering anything; same flash-
        // avoidance pattern as FeatureRoute / the original CapabilityRoute.
        if (!hasLoaded) return null;
        if (feature && !featuresLoaded) return null;

        // Entitlement gate first — a tenant who doesn't have the feature
        // never sees the 403 page (which would imply "you might earn this
        // capability"). Redirect home is the right signal: "this app
        // doesn't include this surface."
        if (feature && !features?.[feature]) {
          const companyId = props.match?.params?.companyId;
          return (
            <Redirect
              to={{
                pathname: companyId ? `/${companyId}/` : '/',
                state: { featureBlocked: feature, from: props.location },
              }}
            />
          );
        }

        const granted = (() => {
          if (capability) {
            // capability = "time.view.team" → ['time', 'view', 'team']
            const [mod, action, level] = capability.split('.');
            return hasScope(mod, action, level);
          }
          if (requireRole) {
            return Boolean(capabilities[requireRole]);
          }
          // No gate specified → admit. Logged so misconfigurations surface.
          // eslint-disable-next-line no-console
          console.warn('<CapabilityRoute> missing required prop: capability or requireRole');
          return true;
        })();

        if (granted) return <Component {...props} />;

        // Phase 1.6 telemetry — capability-route 403. Console.info matches
        // the existing structured-prefix pattern (`[event-name] k=v ...`)
        // so SREs can grep + tally in browser-log aggregation. Real metrics
        // backend can replace this later. Renders only when blocked, so
        // the line is the rate of capability denials per route — should be
        // near-zero post-Phase-2.0 sidebar gating; nonzero indicates
        // bookmark/typed-URL hits or a FE-state bug.
        // eslint-disable-next-line no-console
        console.info(
          `[capability-route-403] route=${rest.path || ''} capability=${capability || ''} requireRole=${requireRole || ''} module=${blockedModule || ''}`,
        );

        if (redirectTo) {
          // Allow `:companyId` template substitution so callers can keep
          // the prop static across tenant switches.
          const companyId = props.match?.params?.companyId;
          const target = companyId ? redirectTo.replace(':companyId', companyId) : redirectTo;
          return <Redirect to={{ pathname: target, state: { capabilityBlocked: capability || requireRole, from: props.location } }} />;
        }

        // Default — render the contextual 403 page.
        const moduleForCopy = blockedModule || (capability ? capability.split('.')[0] : null);
        return <CapabilityForbidden module={moduleForCopy} />;
      }}
    />
  );
};

export default CapabilityRoute;
